import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, ChevronRight, ChevronLeft, Plus, X, Loader2,
  Quote, Link2, Unplug, MapPin, Users, ExternalLink, RefreshCcw,
  CalendarPlus, Sparkles
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import { FEATURE_CALENDAR_INTEGRATION } from '../utils/featureFlags';

const VALOR_COLOR = {
  'Profesionalismo':  'from-blue-600 to-indigo-600',
  'Rigurosidad':      'from-slate-600 to-slate-800',
  'Compromiso':       'from-rose-600 to-pink-600',
  'Resolución':       'from-amber-600 to-orange-600',
  'Calidad de Vida':  'from-emerald-600 to-teal-600',
  'Colaboración':     'from-violet-600 to-purple-600',
  'Desarrollo Sostenible': 'from-green-600 to-lime-600',
  'Eficiencia Económica':  'from-cyan-600 to-blue-600',
};

const CalendarioPeritumWidget = () => {
  // 🔒 Feature flag: integración de Google Calendar pausada temporalmente.
  // (Ver /app/frontend/src/utils/featureFlags.js).
  if (!FEATURE_CALENDAR_INTEGRATION) {
    return null;
  }
  return <CalendarioPeritumWidgetInner />;
};

const CalendarioPeritumWidgetInner = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [valorDia, setValorDia] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/oauth/calendar/status');
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  const fetchValor = useCallback(async () => {
    try {
      const r = await api.get('/cultura/valor-del-dia');
      setValorDia(r.data);
    } catch {
      setValorDia(null);
    }
  }, []);

  const fetchEventos = useCallback(async () => {
    if (!status.connected) return;
    setLoading(true);
    try {
      const r = await api.get('/calendar/events');
      setEventos(r.data.events || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando eventos');
    } finally {
      setLoading(false);
    }
  }, [status.connected]);

  useEffect(() => { fetchStatus(); fetchValor(); }, [fetchStatus, fetchValor]);
  useEffect(() => {
    if (status.connected && !collapsed) fetchEventos();
  }, [status.connected, collapsed, fetchEventos]);

  // Detectar callback OAuth en query string al volver de Google
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_calendar') === 'ok') {
      toast.success(`Google Calendar conectado: ${params.get('email') || ''}`);
      fetchStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('google_calendar') === 'error') {
      const msg = params.get('msg') || '';
      const email = params.get('email') || '';
      if (msg === 'email_no_corporativo') {
        toast.error(
          `Solo se permite conectar cuentas @peritum.cl. La cuenta "${email}" no es válida. Por favor, sal de tu sesión Google personal y vuelve a intentar.`,
          { duration: 9000 }
        );
      } else {
        toast.error(`Error conectando Google Calendar: ${msg}`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus]);

  const conectar = async () => {
    try {
      const r = await api.get('/oauth/calendar/start');
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(formatErrorMessage(e) || 'Error iniciando OAuth');
    }
  };

  const desconectar = async () => {
    if (!window.confirm('¿Desconectar Google Calendar?')) return;
    try {
      await api.delete('/oauth/calendar/disconnect');
      toast.success('Desconectado');
      setEventos([]);
      fetchStatus();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  // Próximo evento (siguiente desde ahora)
  const proximo = eventos.find(e => new Date(e.start) >= new Date());
  const hoy = new Date().toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });

  const gradient = VALOR_COLOR[valorDia?.valor] || 'from-slate-700 to-slate-900';

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className={`fixed right-3 top-1/2 -translate-y-1/2 z-40 bg-gradient-to-br ${gradient} text-white rounded-l-xl shadow-lg hover:shadow-xl transition-all p-2 group`}
        title="Calendario Peritum"
        data-testid="cal-widget-toggle"
      >
        <div className="flex flex-col items-center gap-1">
          <Calendar className="w-5 h-5" />
          <span className="text-[9px] font-bold uppercase tracking-wider">Cal</span>
          {proximo && <Badge className="bg-white text-slate-900 text-[9px] h-3.5 px-1 mt-1">1+</Badge>}
        </div>
      </button>
    );
  }

  return (
    <>
      <div
        className="fixed right-0 top-0 bottom-0 w-96 max-w-full bg-white shadow-2xl border-l-2 border-slate-200 z-40 flex flex-col"
        data-testid="cal-widget-expanded"
      >
        {/* Header con Valor del Día */}
        <div className={`bg-gradient-to-br ${gradient} text-white p-3.5 pb-3 relative`}>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="absolute top-2 right-2 text-white/80 hover:text-white p-1 rounded hover:bg-white/20"
            data-testid="cal-widget-close"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wider opacity-90">
            <Quote className="w-3 h-3" /> Valor del día
          </div>
          <p className="font-bold text-lg leading-tight">{valorDia?.valor || '—'}</p>
          <p className="text-xs leading-snug opacity-95 mt-1 italic">"{valorDia?.mensaje || ''}"</p>
          <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/20">
            <p className="text-xs font-medium capitalize">{hoy}</p>
            <Calendar className="w-4 h-4 opacity-80" />
          </div>
        </div>

        {/* Cuerpo */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {!status.configured && (
            <Card className="p-3 bg-amber-50 border-amber-200 text-xs">
              <p className="font-semibold text-amber-900 mb-1">⚠ Configuración pendiente</p>
              <p className="text-amber-800">
                Para activar el calendario, el administrador debe configurar las credenciales OAuth de Google
                (<code>GOOGLE_CLIENT_ID</code> y <code>GOOGLE_CLIENT_SECRET</code>) en backend/.env.
              </p>
            </Card>
          )}

          {status.configured && !status.connected && (
            <Card className="p-4 text-center border-slate-200">
              <Calendar className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-800 mb-1">Conecta tu Google Calendar</p>
              <p className="text-xs text-slate-600 mb-3">
                Visualiza tus eventos y agenda reuniones sin salir del ERP.
              </p>
              <Button onClick={conectar} className="bg-slate-900 hover:bg-slate-800 text-white w-full" data-testid="cal-widget-connect">
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Conectar Google Calendar
              </Button>
            </Card>
          )}

          {status.connected && (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-slate-500 truncate flex-1">
                  <span className="font-semibold text-emerald-700">●</span> {status.email}
                </div>
                <Button size="sm" variant="ghost" className="h-6 px-2" onClick={fetchEventos} title="Refrescar">
                  <RefreshCcw className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-600" onClick={desconectar} title="Desconectar">
                  <Unplug className="w-3 h-3" />
                </Button>
              </div>

              <Button
                onClick={() => setCreateOpen(true)}
                className="w-full bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700"
                data-testid="cal-widget-create"
              >
                <CalendarPlus className="w-4 h-4 mr-1.5" /> Agendar reunión
              </Button>

              {loading ? (
                <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
              ) : eventos.length === 0 ? (
                <Card className="p-4 text-center border-dashed border-slate-300">
                  <p className="text-xs text-slate-500">Sin eventos esta semana 🎉</p>
                </Card>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Esta semana</p>
                  {eventosPorDia(eventos).map(({ fecha, label, items }) => (
                    <div key={fecha} className="border-l-2 border-slate-200 pl-2.5 mb-2">
                      <p className="text-[10px] uppercase font-bold text-slate-700">{label}</p>
                      {items.map(ev => <EventoCard key={ev.id} ev={ev} onDeleted={fetchEventos} />)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {createOpen && (
        <CreateEventModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchEventos(); }} />
      )}
    </>
  );
};

const eventosPorDia = (eventos) => {
  const grupos = {};
  eventos.forEach(ev => {
    const d = new Date(ev.start);
    const fecha = d.toISOString().slice(0, 10);
    if (!grupos[fecha]) {
      const isHoy = fecha === new Date().toISOString().slice(0, 10);
      grupos[fecha] = {
        fecha,
        label: isHoy ? 'Hoy' : d.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'short' }),
        items: []
      };
    }
    grupos[fecha].items.push(ev);
  });
  return Object.values(grupos);
};

const EventoCard = ({ ev, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);
  const start = new Date(ev.start);
  const end = new Date(ev.end);
  const horaIni = ev.all_day ? 'Todo el día' : start.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  const horaFin = ev.all_day ? '' : end.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

  const eliminar = async (e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.delete(`/calendar/events/${ev.id}`);
      toast.success('Eliminado');
      if (onDeleted) onDeleted();
    } catch (err) { toast.error(formatErrorMessage(err)); }
  };

  return (
    <button
      type="button"
      onClick={() => setExpanded(v => !v)}
      className="w-full text-left bg-white border border-slate-200 rounded-md p-2 mt-1 hover:shadow-sm transition"
      data-testid={`cal-event-${ev.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="text-[10px] font-bold text-slate-600 w-12 flex-shrink-0 pt-0.5">
          {horaIni}
          {horaFin && <div className="text-slate-400 font-normal">{horaFin}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-900 truncate">{ev.summary}</p>
          {ev.location && <p className="text-[10px] text-slate-500 truncate flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {ev.location}</p>}
          {ev.attendees?.length > 0 && <p className="text-[10px] text-slate-500 flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {ev.attendees.length} asistente{ev.attendees.length === 1 ? '' : 's'}</p>}
        </div>
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-1 text-[11px] text-slate-700">
          {ev.description && <p className="whitespace-pre-line">{ev.description}</p>}
          {ev.hangout_link && (
            <a href={ev.hangout_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
              <ExternalLink className="w-3 h-3" /> Unirse a Google Meet
            </a>
          )}
          <div className="flex items-center justify-between gap-1 pt-1">
            {ev.html_link && (
              <a href={ev.html_link} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                <ExternalLink className="w-2.5 h-2.5" /> Abrir en Google
              </a>
            )}
            <button type="button" onClick={eliminar} className="text-[10px] text-rose-600 hover:underline">Eliminar</button>
          </div>
        </div>
      )}
    </button>
  );
};

const CreateEventModal = ({ onClose, onCreated }) => {
  const todayStr = new Date().toISOString().slice(0, 16);
  const [form, setForm] = useState({
    title: '',
    start: todayStr,
    end: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
    description: '',
    location: '',
    attendees: ''
  });
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!form.title.trim()) {
      toast.error('Título obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        description: form.description,
        location: form.location,
        attendees: form.attendees.split(',').map(e => e.trim()).filter(Boolean),
      };
      await api.post('/calendar/events', payload);
      toast.success('Evento creado');
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cal-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-rose-600" /> Agendar reunión
          </DialogTitle>
          <DialogDescription>Se creará en tu calendario primario de Google.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Título *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus data-testid="cal-create-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Inicio *</Label>
              <Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="cal-create-start" />
            </div>
            <div>
              <Label className="text-xs">Fin *</Label>
              <Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="cal-create-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Ubicación</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Oficina · Zoom · ..." />
          </div>
          <div>
            <Label className="text-xs">Asistentes (emails separados por coma)</Label>
            <Input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="socio@peritum.cl, cliente@..." />
          </div>
          <div>
            <Label className="text-xs">Descripción</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="cal-create-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</> : <><Sparkles className="w-4 h-4 mr-2" /> Crear evento</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarioPeritumWidget;
